/**
 * Shared price formatting and normalization.
 *
 * Single source of truth for how prices are displayed across the product.
 * Every component that shows a price should import from here instead of
 * hardcoding "/week", "/session", etc.
 */

/**
 * The price-unit domain, stated ONCE at runtime so every other statement of it can be
 * checked against this one instead of being re-typed.
 *
 * #2125 item 1 added `per_group` and `per_year`. The domain is restated in six other
 * places — three SQL CHECKs (camps m173, camp_pricing_tiers m174, camp_price_snapshots
 * m509/m535), the extraction enum, the two LLM response narrowers, and the writer's tier
 * filter — and a value that is legal in one and illegal in another is silently DROPPED at
 * write time (the regfox `'program'` failure class: a 23514 swallowed by a console.warn).
 * `tests/contracts/price-model-honesty-contract.test.ts` compares all of them against this
 * array, which is why it is a runtime value and the type is derived from it rather than the
 * other way round.
 */
export const PRICE_UNITS = [
  "per_day",
  "per_week",
  "per_session",
  "per_month",
  "per_group",
  "per_year",
  "total",
] as const;

export type PriceUnit = (typeof PRICE_UNITS)[number];

const UNIT_SUFFIXES: Record<PriceUnit, string> = {
  per_day: "/day",
  per_week: "/week",
  per_session: "/session",
  per_month: "/month",
  // #2125 item 1 — a fee that buys a whole GROUP, not one child's place. Cool Kids
  // District's $19 is the live example the issue names; before this it could only be
  // stored as a per-child period, which is where "$19/week" came from.
  per_group: "/group",
  // #2125 item 1 — an annual membership or tuition year. Distinct from `total`, which
  // means "the whole of THIS program"; a year covers programs we are not looking at.
  per_year: "/year",
  total: " total",
};

/**
 * Units whose amount CANNOT be compared against a parent's price cap, because the amount
 * does not price one child for one bounded stretch of the summer.
 *
 * `per_group` prices a whole group and we never learn the group size; `per_year` prices
 * twelve months of membership, most of which is not the camp being filtered for. There is
 * no multiplier that turns either into the per-child figure a "$200" chip means, so the
 * only honest thing a cap can do with them is decline to judge — which is exactly what it
 * already does with a camp holding no price at all.
 *
 * Deliberately NOT the same question as "can this be normalised to a week"
 * (`normalizeToWeeklyCents`): `total` and an unknown-`days_per_week` `per_day` also fail
 * THAT test, yet the live read model has compared them raw since m177 and narrowing that is
 * a separate, visible product change. This set names only the two units that were never
 * comparable under any arithmetic — so it changes the standing of no row that exists today
 * (0 of the catalogue carries either unit) and cannot silently re-file the ones that do.
 */
const CAP_INCOMPARABLE_UNITS: ReadonlySet<string> = new Set<PriceUnit>([
  "per_group",
  "per_year",
]);

/**
 * May a stored amount in this unit be COMPARED against a per-child price cap?
 *
 * True for every unit the product has ever compared, and for `null`/absent — a camp that
 * states no period is compared raw today and this must not quietly change that. False only
 * for the two units of `CAP_INCOMPARABLE_UNITS`.
 *
 * The m535 `search_camps` price cap and price sort ask the SAME question in SQL; the
 * honesty contract pins that the two lists are the same set, so a unit added here without
 * being added there (or vice versa) fails the build rather than mis-ranking a camp.
 */
export function priceUnitIsCapComparable(unit?: string | null): boolean {
  if (unit == null) return true;
  return !CAP_INCOMPARABLE_UNITS.has(unit);
}

/**
 * At the TIER grain, `per_week` is still the one value that carries no information
 * about the row storing it: `camp_pricing_tiers.price_unit` (m174) is still declared
 * `DEFAULT 'per_week'`, so an omitting writer stores it to this day. That is the
 * whole justification for the tier-side value rule below — and ONLY the tier side.
 * `camps.price_unit` lost its default in m514 and, since m526, carries an explicit
 * witness column instead, so the camp-side gate no longer reasons from the value at
 * all (see observedPriceUnit).
 */
const COLUMN_DEFAULT_UNIT: PriceUnit = "per_week";

/** Membership test that cannot be fooled by an inherited property (`"toString"`). */
const KNOWN_UNITS: ReadonlySet<string> = new Set(Object.keys(UNIT_SUFFIXES));

/**
 * `camps.price_unit_source` values the render gate trusts (m526's closed vocabulary).
 *
 *   'derived_iso_week' — deriveBprdSectionPriceUnit produced the unit: every priced
 *       section of the camp spans one ISO week (per_week) or more than one (total).
 *       Deterministic, computed from the partner API's own section dates, and
 *       re-derived after every port apply (lib/camps/price-unit-witness.ts) — the
 *       most trustworthy period in the system, and the one #2294 C showed the old
 *       value rule suppressed.
 *
 *   'extracted' — the LLM extraction AFFIRMATIVELY returned the unit. This is a
 *       trusted witness for exactly the reason the honesty contract pins at the
 *       schema: `lib/ai/schemas/camp-extraction.ts` admits null, so a returned unit
 *       is a positive statement the model was free not to make — #2294's own section
 *       C makes this argument for the extracted `per_week`. If that schema ever
 *       stops admitting null, the contract turns red and this entry must be
 *       re-argued, because every emitted unit becomes a forced guess.
 *
 * Anything else — NULL above all — means nobody has witnessed the stored unit, and
 * the gate prints no period whatever the value says.
 */
const TRUSTED_UNIT_WITNESSES: ReadonlySet<string> = new Set([
  "derived_iso_week",
  "extracted",
]);

/** The two camp columns the period gate reads: the stored unit and its m526 witness. */
export type CampPriceUnitSource = {
  price_unit?: string | null;
  price_unit_source?: string | null;
};

/**
 * The stored price unit a PARENT-FACING surface may print, or `null` when nothing
 * witnessed the stored value.
 *
 * ONE RULE, SYMMETRIC (#2294): a stored period prints exactly when a trusted witness
 * (`price_unit_source`, m526) says a real process produced it — whatever the value —
 * and is withheld otherwise — whatever the value.
 *
 * WHY THIS REPLACED THE VALUE RULE. Until m526 this function keyed on the VALUE:
 * `per_week` was withheld (indistinguishable from m173's DEFAULT residue) and any
 * other value printed ("the writer decided"). #2294 showed both halves wrong at once:
 *
 *   B. An uncorroborated `per_month`/`per_session` printed as fact. Provenance
 *      absence could not gate it — all 143 `price_unit` field_provenance rows come
 *      from ONE writer (`scrapers.shared_writer.llm_extract`), so absence is
 *      evidence about which writer ran, not about whether a period was read. The
 *      witness column exists to be the signal provenance could not be.
 *   C. The deterministically derived BPRD `per_week` (one ISO-week test over the
 *      partner's own section dates) was suppressed for its spelling. With the
 *      witness, `per_week` + 'derived_iso_week' prints like any other witnessed
 *      period.
 *
 * LIVE EFFECT at the flip: none visible. 0 published camps print a period today
 * (all published rows store `per_week`-or-NULL, which the old rule suppressed), and
 * every existing row starts with a NULL witness — so nothing appears until a
 * producer stamps a witness, and nothing true disappears. The BPRD daily sync then
 * lights up the derived periods the old rule was hiding — that is C landing.
 *
 * COST, stated plainly: a legacy unit whose witness is unknowable stays unprinted
 * until its writer re-runs and stamps one. `formatPrice` already states the trade —
 * saying less is the cost of not saying something untrue.
 *
 * This also VALIDATES the value: anything outside `UNIT_SUFFIXES` (m197 once
 * permitted a `per_hour` that m173 never did) returns null rather than reaching
 * `UNIT_SUFFIXES[unit]` and rendering "$250undefined". A junk WITNESS is treated
 * exactly like no witness. That is why callers hand this the row's raw columns
 * instead of casting either one.
 */
export function observedPriceUnit(camp: CampPriceUnitSource): PriceUnit | null {
  const unit = camp.price_unit;
  if (!unit || !KNOWN_UNITS.has(unit)) return null;
  const witness = camp.price_unit_source;
  if (witness == null || !TRUSTED_UNIT_WITNESSES.has(witness)) return null;
  return unit as PriceUnit;
}

/** The two `camp_pricing_tiers` columns that decide whether a tier's period may print. */
export type TierPriceUnitSource = {
  price_unit?: string | null;
  source?: string | null;
};

/**
 * `camp_pricing_tiers.source` values whose writer CANNOT decline to state a period, so
 * the period it stored is a forced guess rather than an observation.
 *
 * `enrichment` is the whole set today. `lib/ai/schemas/camp-enrichment.ts` declares the
 * TIER `price_unit` as `{ type: "string", enum: [five units] }` and lists it in the
 * item's `required` — no null anywhere — so the model must attach a period to every tier
 * it emits whether or not the page states one. Its own comment says #2199 left it that
 * way on purpose. The camp-level `price_unit` in the same file DOES admit null, and that
 * contrast is the entire justification for treating the two differently; the honesty
 * contract asserts both shapes so this set expires on its own when the schema changes.
 */
const FORCED_TIER_UNIT_SOURCES: ReadonlySet<string> = new Set(["enrichment"]);

/**
 * The TIER-grain value rule, kept when m526 moved the camp gate onto witnesses.
 *
 * `camp_pricing_tiers` has no witness column, and does not need one to keep its old
 * honesty properties: m174's `DEFAULT 'per_week'` is STILL live on the tier table (the
 * honesty contract pins it), so a stored tier `per_week` remains indistinguishable from
 * an omitting writer and is withheld, while a non-default tier value from a writer that
 * could decline was a choice. That is the pre-m526 `observedPriceUnit` body, verbatim —
 * it stopped being right for camps when m514 dropped THEIR default, and it stays right
 * here for exactly as long as the tier default stands.
 */
function tierValueGatedUnit(unit?: string | null): PriceUnit | null {
  if (!unit) return null;
  if (unit === COLUMN_DEFAULT_UNIT) return null;
  return KNOWN_UNITS.has(unit) ? (unit as PriceUnit) : null;
}

/**
 * The period a PRICING TIER may print, or null.
 *
 * #2294 — a value rule alone gets this row wrong in the direction that produces a
 * false statement rather than an omission. Measured against prod on 2026-08-31 with the
 * anon key (`camp_pricing_tiers` is anon-readable; 401 rows, error field checked):
 *
 *   | source     | per_week | per_day | per_month | per_session | total |
 *   |------------|---------:|--------:|----------:|------------:|------:|
 *   | scraped    |      215 |       8 |         0 |           0 |    11 |
 *   | enrichment |       50 |      11 |        26 |          43 |    37 |
 *
 * 136 tiers print a period under the value rule alone, and 117 of them (86%) come from
 * the writer that had no way to say "this tier states no period". The mirror of the
 * `per_week` suppression is to withhold those too: the reason we do not print a defaulted
 * `per_week` is that nobody chose it, and nobody chose these either.
 *
 * The `scraped` tiers are not independently trusted — `syncCampPricingTiersFromWeeks`
 * copies `camps.price_unit` straight into the tier — so they take the tier value rule
 * (per_week suppressed as possible m174 default; a stated non-default value from a
 * writer free to omit was a choice). A tier a HUMAN wrote (`provider`, `admin`) is a
 * statement someone made and keeps its period. Note the DELIBERATE asymmetry with the
 * m526 camp gate: a camp's uncorroborated `per_month` is suppressed while a scraped
 * tier's prints — the tier column still has the live DEFAULT that makes "not the
 * default" mean something, and the tier row carries the one witness a tier can carry
 * (`source`). Extending the m526 witness to tiers is tracked in #2125's remainder.
 *
 * Live effect of the source half: 117 tier rows across 56 camps stop printing an
 * unstated period. None are published today (all 106 tiers on published camps are
 * `scraped`/`per_week`, already suppressed), so this changes no pixel now and cannot
 * become a lie later.
 */
export function observedTierPriceUnit(tier: TierPriceUnitSource): PriceUnit | null {
  if (tier.source != null && FORCED_TIER_UNIT_SOURCES.has(tier.source)) return null;
  return tierValueGatedUnit(tier.price_unit);
}

/**
 * The section-level prices that may speak for the CAMP, given all of them.
 *
 * #2302 — `$0.00` is a legitimate price for a SECTION and a lie about the camp that
 * contains it. BPRD files volunteer slots and goalie registrations as $0.00/$0.00
 * sections of a paid ActivityCode, every camp-level rollup in the product takes the
 * minimum across sections, and `formatPrice(0)` renders the result as the literal word
 * "Free" — with no publish warning, because `lib/camps/validate.ts` fires its
 * missing-price warning on NULL and a fabricated 0 is not null. Twelve ActivityCodes in
 * the live feed already have this shape.
 *
 * The rule is one sentence: a free section is dropped from the camp-level candidates
 * ONLY when a paid sibling exists. A genuinely free camp — every section $0 — keeps its
 * zero and still renders "Free", because there the zero IS what the source says about the
 * camp. Nothing here touches the sections themselves; they keep their real prices.
 *
 * Generic over the row so the ONE statement of the rule serves both the BPRD normalizer
 * (which holds ID/OD fee pairs) and the camp_weeks rollup (which holds bare integers) —
 * the two places that independently produced the same $0 headline.
 */
export function campLevelPriceCandidates<T>(
  rows: readonly T[],
  centsOf: (row: T) => number,
): T[] {
  const paid = rows.filter((row) => centsOf(row) > 0);
  return paid.length > 0 ? paid : [...rows];
}

/**
 * Format price with unit suffix: "$250/week", "$75/day", "Price varies".
 */
export function formatPrice(
  cents: number | null,
  unit?: PriceUnit | null,
): string {
  if (cents === null || cents === undefined) return "Price varies";
  if (cents === 0) return "Free";
  const dollars = Math.round(cents / 100);
  // #2125 — an unknown unit renders as a bare price, not as "/week".
  //
  // This used to be `UNIT_SUFFIXES[unit ?? "per_week"]`, so a camp whose period we never
  // learned was still shown to parents with a confident "/week" on it. That is the one
  // direction this function must not be wrong in: "$19" for a $19-per-GROUP activity is
  // merely incomplete, while "$19/week" is false, and a $58/month afterschool program shown
  // as "$58/week" understates the real cost by more than four times.
  //
  // Saying less is the cost of not saying something untrue.
  const suffix = unit ? UNIT_SUFFIXES[unit] : "";
  return `$${dollars}${suffix}`;
}

/**
 * Format a stored price RANGE: "$364–$515", or "$289" when the two bounds
 * collapse to the same figure, or "Varies" when either bound is missing.
 *
 * Hoisted verbatim (#2200) from app/components/camp-detail-v2/action-card.tsx,
 * which had the only range formatter in the product. The detail page and the
 * finder card must not spell a range two different ways, and the way to
 * guarantee that is one function rather than two that happen to agree today.
 *
 * No period suffix, ever. A range is two figures; welding "/week" onto the pair
 * would multiply the fabrication #2125 removed by two. The detail page already
 * renders its range without one (it gates priceUnitLabel on price_cents, which
 * a range row does not use), so this preserves that behaviour exactly.
 */
export function formatPriceRange(min: number | null, max: number | null): string {
  if (min === null || max === null) return "Varies";
  if (min === max) return `$${(min / 100).toFixed(0)}`;
  return `$${(min / 100).toFixed(0)}–$${(max / 100).toFixed(0)}`;
}

/** The stored price columns any card-shaped row carries. */
export type PriceFigureSource = {
  price_cents?: number | null;
  price_min_cents?: number | null;
  price_max_cents?: number | null;
};

/**
 * What a row's stored price actually IS — a range, one figure, or nothing.
 *
 * `null` means we hold no figure we are willing to print, which is a different
 * statement from "this is free" (price_cents = 0, a point) and from "we know a
 * floor" (see below).
 */
export type ResolvedPriceFigure =
  | { kind: "range"; minCents: number; maxCents: number }
  | { kind: "point"; cents: number }
  | null;

/**
 * #2200 — resolve the stored price columns to the ONE fact a surface renders.
 *
 * A point price and a genuine range are different facts and must resolve
 * differently, in this order:
 *
 *  1. A GENUINE RANGE — both bounds stored and different. It wins over
 *     price_cents, which is measurably the range's collapsed FLOOR: across all
 *     796 camps in prod, 55 rows carry both a scalar and a genuine range, and in
 *     50 of them price_cents === price_min_cents (e.g. cents=4300 with
 *     min=4300 max=8700). Printing the scalar there understates what a family
 *     pays by the entire width of the range — the exact collapse #2125 removed
 *     from the write path, re-introduced on the read path.
 *  2. A POINT PRICE — price_cents when it is stored, otherwise a pair of bounds
 *     that collapse to the same figure. price_cents is preferred over collapsed
 *     bounds so that every row already printing a figure keeps printing the same
 *     one (195 rows in prod hold both; 191 agree).
 *  3. NOTHING. Including the case where exactly ONE bound is stored: a floor is
 *     not a price. Printing "$199" for a camp we only know starts at $199 is the
 *     same understatement as flattening a range, and unbounded above. Prod holds
 *     zero such rows today (0 of 796), so this branch changes no pixel — it
 *     exists so that a future one-sided write cannot quietly become a claim.
 */
export function resolvePriceFigure(price: PriceFigureSource): ResolvedPriceFigure {
  const min = price.price_min_cents ?? null;
  const max = price.price_max_cents ?? null;
  if (min !== null && max !== null && min < max) {
    return { kind: "range", minCents: min, maxCents: max };
  }
  const point = price.price_cents ?? (min !== null && min === max ? min : null);
  if (point === null || point === undefined) return null;
  return { kind: "point", cents: point };
}

/**
 * The label a card prints for a stored price, or null when there is none to
 * print (the caller renders its own "not listed" copy — the finder's is
 * "Price not listed").
 *
 * `unit` is passed through to formatPrice for the POINT branch only, and the
 * range branch renders no period at all. Callers decide whether a unit may be
 * shown; see the gate in app/components/camp-card.tsx.
 */
export function resolvePriceLabel(
  price: PriceFigureSource,
  unit?: PriceUnit | null,
): string | null {
  const figure = resolvePriceFigure(price);
  if (figure === null) return null;
  if (figure.kind === "range") return formatPriceRange(figure.minCents, figure.maxCents);
  return formatPrice(figure.cents, unit);
}

/**
 * The two stored-bound keys a card-shaped object carries, normalized to null.
 *
 * #2233 — every `<CampCard>` caller hand-builds its card object field by field,
 * and the only reason 53 of the 71 published camps still read "Price not listed"
 * outside the finder is that those literals never grew the two keys M508 added.
 * Spreading this into the map is one import instead of one more chance to forget,
 * and it puts the `?? null` DEPLOYMENT-SKEW CONTRACT in one place rather than
 * nineteen: a row that predates M508 (or a `search-results` cache entry written
 * by the previous deploy) arrives with neither key, and absent must read as "no
 * range known" — never as a crash, and never as $0.
 *
 * It deliberately does NOT resolve anything. The point-vs-range decision belongs
 * to resolvePriceFigure, which the card calls; this only carries the facts there.
 */
export function campCardPriceBounds(price: PriceFigureSource): {
  price_min_cents: number | null;
  price_max_cents: number | null;
} {
  return {
    price_min_cents: price.price_min_cents ?? null,
    price_max_cents: price.price_max_cents ?? null,
  };
}

/**
 * The HIGHEST figure the resolved price can reach, or null when no figure is
 * printed at all. Derived from resolvePriceFigure so it cannot disagree with
 * what resolvePriceLabel renders.
 *
 * Used by the finder's price-cap honesty partition: a row whose ceiling exceeds
 * an active cap has not been verified as under it, and must not sort in with the
 * rows that have.
 */
export function resolvedPriceCeilingCents(price: PriceFigureSource): number | null {
  const figure = resolvePriceFigure(price);
  if (figure === null) return null;
  return figure.kind === "range" ? figure.maxCents : figure.cents;
}

/**
 * What `isVerifiedUnderPriceCap` reads: the stored bounds AND the stored period.
 *
 * `price_unit` is optional for the same deployment-skew reason the bounds are — a cached
 * payload from a previous deploy carries no key — and absent reads as "no period stated",
 * which is comparable (today's behaviour). Every caller that HOLDS the unit must pass it:
 * the honesty contract pins both call sites per instance, because an object that silently
 * omits the key is how a unit-aware predicate goes back to being unit-blind.
 */
export type PriceCapSource = PriceFigureSource & { price_unit?: string | null };

/**
 * Has this row been VERIFIED as costing at or under `capCents`?
 *
 * False for a row whose resolved ceiling exceeds the cap, and false for a row
 * that resolves to no figure at all — "we hold no price" is not evidence of
 * being cheap. The two are deliberately one predicate: both are rows a
 * price-capped surface must not present as if the cap had been applied to them.
 *
 * #2233 hoists this out of the finder (`load-finder-data.ts`, consensus decision
 * 3 condition 4, widened by #2200) so the finder's price chip and the
 * /camps/under/$N landing page cannot drift into two different answers about
 * whether a given camp is under a given cap. It answers only that question; it
 * removes nothing and reorders nothing on its own.
 */
export function isVerifiedUnderPriceCap(
  price: PriceCapSource,
  capCents: number,
): boolean {
  // #2125 item 1 — a number in a unit the cap cannot compare has not been verified as
  // under it, however small it is. $19 PER GROUP is not $19 per child per week, and
  // ranking it into the "Under $200" bucket is the same false claim as flattening a
  // range to its floor, made from the unit side instead of the figure side.
  if (!priceUnitIsCapComparable(price.price_unit)) return false;
  const ceiling = resolvedPriceCeilingCents(price);
  return ceiling !== null && ceiling <= capCents;
}

/**
 * Format price without unit suffix: "$250", "Varies".
 */
export function formatPriceDollarsOnly(cents: number | null): string {
  if (cents === null || cents === undefined) return "Varies";
  if (cents === 0) return "Free";
  return `$${Math.round(cents / 100)}`;
}

/**
 * Get just the unit suffix: "/week", "/day", "/session".
 */
export function priceUnitLabel(unit?: PriceUnit | null): string {
  // #2125 — empty for an unknown unit, for the same reason formatPrice omits the suffix.
  return unit ? UNIT_SUFFIXES[unit] : "";
}

/**
 * Get the human-readable unit label without slash: "per week", "per day".
 */
export function priceUnitLabelLong(unit?: PriceUnit | null): string {
  // #2125 — no invented period. Callers render nothing rather than "per week".
  return unit ? unit.replace("_", " ") : "";
}

/**
 * Normalize a price to weekly cents for apples-to-apples comparison, so that a
 * $75/day camp meeting 5 days can be compared against a $300/week camp.
 *
 * Returns null when the weekly figure CANNOT be derived from what we know —
 * "total" (no duration) and, since #2196, `per_day` with an unknown
 * `days_per_week`.
 *
 * That second case used to `?? 5`, which invented a five-day week for any camp
 * whose schedule we never learned: a $25 single-day ticket came back as $125.
 * In prod that default fires for 4 of the 6 `per_day` camps, whose
 * `days_per_week` is NULL — and Lilypad, which emits `per_day` from labels like
 * "Day Pass ($25/day)", writes no `days_per_week` at all. Inventing the
 * multiplier is the same untruth as inventing the period (#2125): a price we
 * cannot fully describe is described incompletely, never wrongly.
 *
 * NOTE: nothing normalizes on the read path today. The live `search_camps`
 * price filter compares raw `price_cents` (m177 onward — m173's weekly
 * normalization was superseded hours after it shipped), and `search_camps_mv`
 * carries no `weekly_price_cents` column. This helper is the last place the
 * five-day assumption survived.
 */
export function normalizeToWeeklyCents(
  cents: number,
  unit: PriceUnit,
  daysPerWeek?: number | null,
): number | null {
  switch (unit) {
    case "per_day":
      // Multiply only by a number the camp actually told us.
      return daysPerWeek == null ? null : cents * daysPerWeek;
    case "per_week":
      return cents;
    case "per_session":
      return cents; // session ≈ week for most camps
    case "per_month":
      return Math.round(cents / 4.33);
    case "per_group":
      // #2125 item 1 — a group fee divided by an unknown group size is not a number.
      // Inventing a group of four here would be the `?? 5` mistake #2196 removed from
      // the per_day branch, one field over.
      return null;
    case "per_year":
      // #2125 item 1 — a year's membership spread over 52 weeks is arithmetic, not a
      // camp price: most of those weeks are not the program being compared.
      return null;
    case "total":
      return null; // can't normalize without knowing duration
  }
}
